<template>
  <section class="p-3">
    <filtro :isLoading="loading" :advanced="true" @buscar="buscar" />
    <div v-if="kpis.noSales" class="ranking-alert mt-3" role="status">
      <span class="ranking-alert-icon" aria-hidden="true">!</span>
      <div class="ranking-alert-text">
        <strong>{{ noSalesMessage(kpis.noSales) }}</strong>
        <p class="mb-0">Aparecen al final de la tabla, marcados "Sin ventas".</p>
      </div>
      <button type="button" class="btn btn-sm ranking-alert-btn" @click="scrollToNoSales">Ver</button>
    </div>
    <p v-if="rows.length && seesNoSales" class="ranking-login-note mt-2 mb-0">
      El registro de inicios de sesión empieza con esta versión; en fechas anteriores solo aparecen
      doctores con ventas.
    </p>
    <b-card v-if="rows.length" class="mt-3 ranking-visual">
      <div class="ranking-heading">
        <h2 class="h4 mb-0">Ranking visual</h2>
        <p class="ranking-hint mb-0">Click a bar to highlight that doctor in the table.</p>
      </div>
      <div class="ranking-kpis">
        <div class="ranking-kpi">
          <span>Doctors</span>
          <strong>{{ kpis.doctors }}</strong>
          <small v-if="kpis.noSales" class="ranking-kpi-extra">+{{ kpis.noSales }} sin ventas</small>
        </div>
        <div class="ranking-kpi">
          <span>Top Avg NP+NC</span>
          <strong>{{ money(kpis.topAverage) }}</strong>
        </div>
        <div class="ranking-kpi">
          <span>$30 visits</span>
          <strong>{{ kpis.thirtyCount }}</strong>
        </div>
        <div v-if="showIncome" class="ranking-kpi">
          <span>Income</span>
          <strong>{{ money(kpis.incomeTotal) }}</strong>
        </div>
      </div>
      <template v-if="chartRows.length">
      <div class="ranking-measures" role="tablist" aria-label="Chart measure">
        <button
          v-for="measure in measures"
          :key="measure.key"
          type="button"
          class="ranking-measure"
          :class="{ 'is-active': chartMeasure === measure.key }"
          @click="showMeasure(measure.key)"
        >
          {{ measure.label }}
        </button>
      </div>
      <div class="ranking-chart">
        <apexchart
          type="bar"
          :height="chartHeight"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
      <button
        v-if="selectedDoctor"
        type="button"
        class="ranking-selection"
        @click="selectedDoctorId = null"
      >
        {{ selectedDoctor.doctor_name }}
        <span>Clear</span>
      </button>
      </template>
    </b-card>
    <b-card class="mt-3 ranking-card">
      <div class="ranking-heading">
        <h1 class="h4 mb-0">Doctors ranking</h1>
        <div v-if="rows.length" class="ranking-heading-actions">
          <p class="ranking-hint mb-0">Click a column to sort. Numbers start from highest.</p>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="!sortBy && !selectedDoctorId"
            @click="clearTableFilters"
          >
            <i class="fas fa-times mr-1"></i>
            Clear filters
          </button>
          <button type="button" class="btn btn-sm btn-success" @click="downloadExcel">
            <i class="fas fa-file-excel mr-1"></i>
            Excel
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="downloadCsv">
            <i class="fas fa-file-csv mr-1"></i>
            CSV
          </button>
        </div>
      </div>
      <p v-if="loading" class="text-muted mb-0">Loading ranking...</p>
      <p v-else-if="error" class="text-danger mb-0">{{ error }}</p>
      <p v-else-if="!rows.length" class="text-muted mb-0">
        {{ searched ? 'No doctors in this range.' : 'Search a date range to load the ranking.' }}
      </p>
      <div v-else class="table-responsive">
        <table class="table table-sm mb-0 ranking-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="[column.align, { 'is-sorted': sortBy === column.key }]"
              >
                <button type="button" class="ranking-sort-btn" @click="toggleSort(column.key)">
                  <span>{{ column.label }}</span>
                  <i :class="sortIcon(column.key)" aria-hidden="true"></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in tableGroups.withSales"
              :key="`${row.doctor_id}-${index}`"
              :data-doctor-id="row.doctor_id"
              :class="{
                'is-selected': selectedDoctorId === row.doctor_id,
                'is-dimmed': selectedDoctorId && selectedDoctorId !== row.doctor_id,
              }"
              @click="selectDoctor(row)"
            >
              <td>{{ positionText(row) }}</td>
              <td>
                {{ row.doctor_name }}
                <span v-if="partialTag(row)" class="ranking-tag-part">{{ partialTag(row) }}</span>
              </td>
              <td class="text-right">{{ money(row.avg_nc_np) }}</td>
              <td class="text-right">{{ averageWithCount(row.avg_np, row.np_count) }}</td>
              <td class="text-right">{{ averageWithCount(row.avg_nc, row.nc_count) }}</td>
              <td class="text-right">{{ row.np_30_count || 0 }}</td>
              <td class="text-right">{{ row.nc_30_count || 0 }}</td>
              <td v-if="showIncome" class="text-right">{{ money(row.income && row.income.total) }}</td>
            </tr>
            <tr v-if="tableGroups.withoutSales.length" ref="noSalesSeparator" class="ranking-sep">
              <td :colspan="columns.length">Iniciaron sesión sin ventas · {{ tableGroups.withoutSales.length }}</td>
            </tr>
            <tr
              v-for="row in tableGroups.withoutSales"
              :key="`no-sales-${row.doctor_id}`"
              :data-doctor-id="row.doctor_id"
              class="is-no-sales"
            >
              <td><span class="ranking-tag-ns">{{ positionText(row) }}</span></td>
              <td>{{ row.doctor_name }}</td>
              <td class="text-right">{{ money(row.avg_nc_np) }}</td>
              <td class="text-right">{{ averageWithCount(row.avg_np, row.np_count) }}</td>
              <td class="text-right">{{ averageWithCount(row.avg_nc, row.nc_count) }}</td>
              <td class="text-right">{{ row.np_30_count || 0 }}</td>
              <td class="text-right">{{ row.nc_30_count || 0 }}</td>
              <td v-if="showIncome" class="text-right">{{ money(row.income && row.income.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>
  </section>
</template>

<script>
import { BCard } from 'bootstrap-vue'
import * as XLSX from 'xlsx'
import filtro from '@/view/pages/dashboard-all/components/filter'
import dashboardApi from '@/core/services/api/dashboard'
import {
  canSeeNoSales,
  noSalesMessage,
  partialTag,
  positionText,
  rankingKpis,
  rankingRecords,
  sortRankingRows,
  splitBySales,
  unwrapRanking,
  visibleRankingRows,
} from './rankingRows'

const MEASURES = [
  { key: 'avg_nc_np', label: 'Avg NP+NC', money: true },
  { key: 'avg_np', label: 'NP', money: true },
  { key: 'avg_nc', label: 'NC', money: true },
  { key: 'np_30_count', label: '$30 NP', money: false },
  { key: 'nc_30_count', label: '$30 NC', money: false },
  { key: 'income', label: 'Income', money: true },
]

function currentRoleId() {
  try {
    const user = JSON.parse(localStorage.getItem('userData') || '{}')
    return Number(user.roles && user.roles[0] && user.roles[0].id)
  } catch (error) {
    return NaN
  }
}

function escapeCsvCell(value) {
  const text = value == null ? '' : String(value)
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

function rankingFilename(start, end, extension) {
  const clean = value => String(value || 'date').replace(/[^\d-]/g, '')
  return `doctors-ranking_${clean(start)}_${clean(end)}.${extension}`
}

function facilityIds(facilities) {
  if (!Array.isArray(facilities)) return []
  return facilities
    .map(item => (item && typeof item === 'object' ? item.id : item))
    .filter(id => id != null && id !== '')
}

export default {
  name: 'DoctorsRanking',
  components: { BCard, filtro },
  data() {
    return {
      loading: false,
      error: '',
      rows: [],
      showIncome: false,
      sortBy: 'avg_nc_np',
      sortDir: 'desc',
      selectedDoctorId: null,
      rangeStart: '',
      rangeEnd: '',
      searched: false,
      seesNoSales: canSeeNoSales(currentRoleId()),
    }
  },
  computed: {
    columns() {
      const columns = [
        { key: 'position', label: 'Position', align: 'text-left' },
        { key: 'doctor_name', label: 'Doctor', align: 'text-left' },
        { key: 'avg_nc_np', label: 'Avg NP+NC', align: 'text-right' },
        { key: 'avg_np', label: 'NP', align: 'text-right' },
        { key: 'avg_nc', label: 'NC', align: 'text-right' },
        { key: 'np_30_count', label: '$30 NP', align: 'text-right' },
        { key: 'nc_30_count', label: '$30 NC', align: 'text-right' },
      ]
      if (this.showIncome) {
        columns.push({ key: 'income', label: 'Income', align: 'text-right' })
      }
      return columns
    },
    sortedRows() {
      return sortRankingRows(this.rows, this.sortBy, this.sortDir)
    },
    tableGroups() {
      return splitBySales(this.sortedRows)
    },
    kpis() {
      return rankingKpis(this.rows)
    },
    measures() {
      return MEASURES.filter(measure => measure.key !== 'income' || this.showIncome)
    },
    chartMeasure() {
      return this.measures.some(measure => measure.key === this.sortBy)
        ? this.sortBy
        : 'avg_nc_np'
    },
    activeMeasure() {
      return this.measures.find(measure => measure.key === this.chartMeasure) || MEASURES[0]
    },
    chartRows() {
      return this.tableGroups.withSales
    },
    chartHeight() {
      return Math.max(280, this.chartRows.length * 28 + 48)
    },
    chartSeries() {
      return [
        {
          name: this.activeMeasure.label,
          data: this.chartRows.map(row => this.measureValue(row, this.chartMeasure) || 0),
        },
      ]
    },
    chartOptions() {
      const rows = this.chartRows
      const measure = this.activeMeasure
      const selectedId = this.selectedDoctorId
      const format = value => this.formatMeasure(value, measure)
      return {
        chart: {
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          fontFamily: 'inherit',
          animations: { easing: 'easeinout', speed: 350 },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.selectDoctor(rows[config.dataPointIndex])
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            distributed: true,
            barHeight: '68%',
            dataLabels: { position: 'top' },
          },
        },
        colors: rows.map(row => {
          const value = this.numeric(row.avg_nc_np)
          const isAbove = value != null && value >= 300
          const base = isAbove ? '#43A047' : '#E53935'
          if (!selectedId) return base
          if (row.doctor_id === selectedId) return isAbove ? '#2e7d32' : '#c62828'
          return isAbove ? '#c8e6c9' : '#ffcdd2'
        }),
        dataLabels: {
          enabled: true,
          offsetX: 24,
          style: { fontSize: '11px', fontWeight: 600, colors: ['#3d4f66'] },
          formatter: value => format(value),
        },
        legend: { show: false },
        grid: { borderColor: '#e6eef6', strokeDashArray: 3 },
        xaxis: {
          categories: rows.map(row => row.doctor_name),
          labels: {
            formatter: value => format(value),
            style: { colors: '#6b7c93', fontSize: '11px' },
          },
        },
        yaxis: {
          labels: {
            style: { colors: '#3d4f66', fontSize: '12px', fontWeight: 600 },
            maxWidth: 160,
          },
        },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const row = rows[dataPointIndex]
            if (!row) return ''
            return `<div class="ranking-tooltip">
              <strong>${row.doctor_name}</strong>
              <div>Position ${positionText(row)}</div>
              <div>Avg NP+NC ${this.money(row.avg_nc_np)}</div>
              <div>NP ${this.averageWithCount(row.avg_np, row.np_count)}</div>
              <div>NC ${this.averageWithCount(row.avg_nc, row.nc_count)}</div>
              <div>$30 ${row.np_30_count || 0} NP · ${row.nc_30_count || 0} NC</div>
              ${this.showIncome ? `<div>Income ${this.money(row.income && row.income.total)}</div>` : ''}
            </div>`
          },
        },
        states: {
          active: { filter: { type: 'none' } },
          hover: { filter: { type: 'lighten', value: 0.08 } },
        },
      }
    },
    selectedDoctor() {
      return this.rows.find(row => row.doctor_id === this.selectedDoctorId) || null
    },
  },
  created() {
    const roleId = currentRoleId()
    if (roleId === 4 || roleId === 5) {
      this.error = 'You do not have permission to view the doctors ranking.'
    }
  },
  methods: {
    noSalesMessage,
    partialTag,
    positionText,
    async buscar({ start, end, facilities }) {
      if (this.error && !start) return
      const roleId = currentRoleId()
      if (roleId === 4 || roleId === 5) return
      this.loading = true
      this.error = ''
      try {
        const response = await dashboardApi.getDoctorsRanking({
          start_date: start,
          end_date: end,
          facilities: facilityIds(facilities),
        })
        const ranking = visibleRankingRows(unwrapRanking(response), roleId)
        this.rows = ranking
        this.searched = true
        this.rangeStart = start
        this.rangeEnd = end
        this.selectedDoctorId = null
        this.showIncome = ranking.some(row => row && Object.prototype.hasOwnProperty.call(row, 'income'))
      } catch (error) {
        this.rows = []
        this.selectedDoctorId = null
        this.showIncome = false
        this.error =
          error?.response?.data?.message || 'Could not load the doctors ranking.'
      } finally {
        this.loading = false
      }
    },
    downloadExcel() {
      const records = rankingRecords(this.sortedRows, this.showIncome)
      if (!records.length) return
      const book = XLSX.utils.book_new()
      const sheet = XLSX.utils.json_to_sheet(records)
      XLSX.utils.book_append_sheet(book, sheet, 'Doctors ranking')
      XLSX.writeFile(book, rankingFilename(this.rangeStart, this.rangeEnd, 'xlsx'))
    },
    downloadCsv() {
      const records = rankingRecords(this.sortedRows, this.showIncome)
      if (!records.length) return
      const keys = Object.keys(records[0])
      const lines = [
        keys.map(escapeCsvCell).join(','),
        ...records.map(record => keys.map(key => escapeCsvCell(record[key])).join(',')),
      ]
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = rankingFilename(this.rangeStart, this.rangeEnd, 'csv')
      link.click()
      URL.revokeObjectURL(url)
    },
    showMeasure(key) {
      this.sortBy = key
      this.sortDir = 'desc'
    },
    selectDoctor(row) {
      if (!row) return
      this.selectedDoctorId = this.selectedDoctorId === row.doctor_id ? null : row.doctor_id
      if (this.selectedDoctorId == null) return
      this.$nextTick(() => {
        const selected = this.$el.querySelector(`[data-doctor-id="${row.doctor_id}"]`)
        if (selected) selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
    measureValue(row, key) {
      if (key === 'income') return this.numeric(row.income && row.income.total)
      if (key === 'np_30_count' || key === 'nc_30_count') return this.numeric(row[key]) || 0
      return this.numeric(row[key])
    },
    formatMeasure(value, measure) {
      if (!measure.money) return Number(value || 0).toLocaleString('en-US')
      return this.money(value)
    },
    clearTableFilters() {
      this.sortBy = null
      this.sortDir = 'desc'
      this.selectedDoctorId = null
    },
    toggleSort(key) {
      const textColumn = key === 'doctor_name' || key === 'position'
      if (this.sortBy !== key) {
        this.sortBy = key
        this.sortDir = textColumn ? 'asc' : 'desc'
        return
      }
      if (textColumn ? this.sortDir === 'asc' : this.sortDir === 'desc') {
        this.sortDir = textColumn ? 'desc' : 'asc'
        return
      }
      this.clearTableFilters()
    },
    sortIcon(key) {
      if (this.sortBy !== key) return 'fas fa-sort ranking-sort-icon is-idle'
      return this.sortDir === 'desc'
        ? 'fas fa-sort-amount-down ranking-sort-icon'
        : 'fas fa-sort-amount-up ranking-sort-icon'
    },
    scrollToNoSales() {
      const separator = this.$refs.noSalesSeparator
      if (separator) separator.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    numeric(value) {
      if (value == null || value === '') return null
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    money(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    averageWithCount(average, count) {
      if (average == null || average === '') return '—'
      return `${this.money(average)} (${count || 0})`
    },
  },
}
</script>

<style scoped>
.ranking-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 1rem;
}

.ranking-hint {
  color: #6b7c93;
  font-size: 0.85rem;
}

.ranking-heading-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.ranking-table thead th {
  background: #f4f7fb;
  border-bottom: 2px solid #d5e2f0;
  vertical-align: middle;
  white-space: nowrap;
}

.ranking-table thead th.is-sorted {
  background: #e7f1fb;
}

.ranking-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3d4f66;
  font-weight: 700;
  cursor: pointer;
}

.ranking-table thead th.text-right .ranking-sort-btn {
  justify-content: flex-end;
}

.ranking-table thead th.is-sorted .ranking-sort-btn {
  color: #1d4f91;
}

.ranking-sort-icon {
  font-size: 0.8rem;
  color: #1d4f91;
}

.ranking-sort-icon.is-idle {
  color: #b7c3d1;
}

.ranking-table tbody tr {
  cursor: pointer;
}

.ranking-table tbody tr:hover {
  background: #f8fbfe;
}

.ranking-table tbody tr.is-selected {
  background: #e7f1fb;
  box-shadow: inset 3px 0 0 #1d4f91;
}

.ranking-table tbody tr.is-dimmed {
  opacity: 0.45;
}

.ranking-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.ranking-kpi {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid #d5e2f0;
  border-radius: 0.6rem;
  background: #f4f7fb;
}

.ranking-kpi span {
  color: #6b7c93;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.ranking-kpi strong {
  color: #1d4f91;
  font-size: 1.15rem;
}

.ranking-measures {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}

.ranking-measure {
  border: 1px solid #d5e2f0;
  border-radius: 999px;
  background: #fff;
  color: #3d4f66;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
}

.ranking-measure.is-active {
  background: #1d4f91;
  border-color: #1d4f91;
  color: #fff;
}

.ranking-chart {
  margin: 0 -0.25rem;
}

.ranking-chart >>> .apexcharts-yaxis,
.ranking-chart >>> .apexcharts-datalabels {
  pointer-events: none;
}

.ranking-chart >>> .apexcharts-bar-area {
  cursor: pointer;
}

.ranking-selection {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.25rem;
  border: 0;
  border-radius: 999px;
  background: #e7f1fb;
  color: #1d4f91;
  font-weight: 700;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
}

.ranking-selection span {
  color: #6b7c93;
  font-weight: 600;
}

.ranking-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #f3c27a;
  border-left: 4px solid #e59a1f;
  border-radius: 0.6rem;
  background: #fff8ec;
}

.ranking-alert-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: #e59a1f;
  color: #fff;
  font-weight: 800;
}

.ranking-alert-text {
  flex: 1;
  min-width: 0;
}

.ranking-alert-text strong {
  color: #8a5300;
}

.ranking-alert-text p {
  color: #7a5a2a;
  font-size: 0.85rem;
}

.ranking-alert-btn {
  border: 1px solid #e59a1f;
  background: #fff;
  color: #8a5300;
  font-weight: 700;
}

.ranking-alert-btn:hover,
.ranking-alert-btn:focus {
  background: #e59a1f;
  color: #fff;
}

.ranking-login-note {
  color: #6b7c93;
  font-size: 0.78rem;
}

.ranking-kpi .ranking-kpi-extra {
  color: #8a5300;
  font-size: 0.75rem;
  font-weight: 700;
}

.ranking-tag-part {
  margin-left: 0.4rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #eef2f7;
  color: #6b7c93;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.ranking-tag-ns {
  display: inline-block;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  background: #ffe9c7;
  color: #8a5300;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}

.ranking-table tbody tr.ranking-sep,
.ranking-table tbody tr.is-no-sales {
  cursor: default;
}

.ranking-table tbody tr.ranking-sep td {
  background: #fff1dc;
  color: #8a5300;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.ranking-table tbody tr.is-no-sales td {
  background: #fffaf2;
  color: #7a6a55;
}

@media (max-width: 767px) {
  .ranking-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ranking-alert {
    flex-wrap: wrap;
  }

  .ranking-alert-btn {
    width: 100%;
  }
}
</style>

<style>
.ranking-tooltip {
  padding: 0.55rem 0.7rem;
  color: #243246;
  font-size: 0.8rem;
  line-height: 1.45;
}

.ranking-tooltip strong {
  display: block;
  margin-bottom: 0.15rem;
  color: #1d4f91;
}
</style>
